import React from 'react';
import ReactSelect from '@components/atoms/ReactSelect';
import {
  ProjectOption,
  SelectOption,
  ALL_PROJECTS_OPTION,
  ALL_PROJECTS_VALUE,
} from '../types';

interface ProjectSelectorProps {
  projectOptions: ProjectOption[];
  selectedProjectId: string | null;
  onChange: (value: any) => void;
  hasSelectedOrganization: boolean;
  organizationId?: string; // Add organizationId to force re-render
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projectOptions,
  selectedProjectId,
  onChange,
  hasSelectedOrganization,
  organizationId,
}) => {
  const options: SelectOption[] = [
    ALL_PROJECTS_OPTION,
    ...projectOptions.map((p) => ({
      label: p.label,
      value: p.value,
    })),
  ];

  const getCurrentValue = (): SelectOption => {
    // If no project is selected or "All Projects" is selected
    if (!selectedProjectId || selectedProjectId === ALL_PROJECTS_VALUE) {
      return ALL_PROJECTS_OPTION;
    }

    // Find the project in the current organization's project options
    const foundProject = projectOptions.find(
      (p) => p.value === selectedProjectId,
    );
    
    // If the selected project is found in current options, return it
    if (foundProject) {
      return { label: foundProject.label, value: selectedProjectId };
    }
    
    // If selected project is not found in current options (e.g., switched companies),
    // return "All Projects" as fallback
    return ALL_PROJECTS_OPTION;
  };

  const getPlaceholder = (): string => {
    if (projectOptions.length === 0) {
      return hasSelectedOrganization
        ? 'No projects available for this company'
        : 'No projects available';
    }

    return hasSelectedOrganization
      ? 'Choose a project from this company...'
      : 'Choose projects from all companies...';
  };

  return (
    <ReactSelect
      key={organizationId || 'no-organization'} // Force re-render when organization changes
      options={options}
      value={getCurrentValue()}
      onChange={onChange}
      placeholder={getPlaceholder()}
      isMulti={false}
      isClearable
      isDisabled={projectOptions.length === 0}
    />
  );
};
